'use client';

import { Children, isValidElement, useCallback, useId, useRef, useState, type ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';

type OptionProps = { value?: string | number; label?: string; disabled?: boolean; children?: ReactNode };
type SelectOption = { value: string; label: string; disabled?: boolean; group?: string };

/** Keep option declarations readable while sharing one accessible menu. */
export function selectOptions(children: ReactNode): SelectOption[] {
  return Children.toArray(children).flatMap(child => {
    if (!isValidElement<OptionProps>(child)) return [];
    if (child.type === 'optgroup') return selectOptions(child.props.children).map(option => ({
      ...option, group: child.props.label, disabled: child.props.disabled || option.disabled,
    }));
    if (child.type !== 'option') return [];
    const text = Children.toArray(child.props.children).join('');
    return [{ value: String(child.props.value ?? text), label: text, disabled: child.props.disabled }];
  });
}

export type SelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  'aria-label': string;
  'aria-describedby'?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  /** Layout only: margins and width. Geometry lives in globals.css. */
  className?: string;
};

export default function Select({ value, onValueChange, children, id, name, required, disabled, className = '', 'aria-label': label, 'aria-describedby': describedBy }: SelectProps) {
  const generatedId = useId();
  const trigger = useRef<HTMLButtonElement | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const options = selectOptions(children);
  const groups = [...new Set(options.map(option => option.group))];
  const selected = options.find(option => option.value === value);
  const errorId = `${generatedId}-error`;
  const setTrigger = useCallback((node: HTMLButtonElement | null) => {
    trigger.current = node;
    // Keep nested menus inside their owning popover's outside-click boundary.
    setContainer(node?.closest<HTMLElement>('[data-select-scope]') ?? null);
  }, []);
  function change(next: string) { setInvalid(false); onValueChange(next); }

  return <span className={`ui-select ${className}`}>
    <SelectPrimitive.Root value={`value:${value}`} onValueChange={next => { if (next.startsWith('value:')) change(next.slice(6)); }} disabled={disabled} open={!disabled && open} onOpenChange={setOpen}>
      <SelectPrimitive.Trigger ref={setTrigger} id={id || generatedId} aria-label={label} aria-describedby={[describedBy, invalid && errorId].filter(Boolean).join(' ') || undefined} aria-required={required || undefined} aria-invalid={invalid || undefined} className="ui-select-trigger">
        <SelectPrimitive.Value>{selected?.label || 'Choose an option'}</SelectPrimitive.Value>
        <SelectPrimitive.Icon className="ui-select-caret"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg></SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal container={container || undefined}>
        <SelectPrimitive.Content position="popper" sideOffset={5} collisionPadding={16} className="ui-select-content" aria-label={label} onEscapeKeyDown={event => event.stopPropagation()}>
          <SelectPrimitive.ScrollUpButton className="ui-select-scroll" aria-hidden="true">⌃</SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="ui-select-viewport">
            {groups.map(group => <SelectPrimitive.Group key={group || 'ungrouped'}>
              {group && <SelectPrimitive.Label className="px-3 pb-1 pt-3 text-xs font-semibold text-muted">{group}</SelectPrimitive.Label>}
              {options.filter(option => option.group === group).map(option => <SelectPrimitive.Item key={option.value} value={`value:${option.value}`} disabled={option.disabled} textValue={option.label} className="ui-select-item">
              <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              <SelectPrimitive.ItemIndicator className="ui-select-indicator"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 4 4L19 6" strokeLinecap="round" strokeLinejoin="round" /></svg></SelectPrimitive.ItemIndicator>
            </SelectPrimitive.Item>)}
            </SelectPrimitive.Group>)}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="ui-select-scroll" aria-hidden="true">⌄</SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
    {/* Real values and native required validation, including the empty option.
        Radix's internal values are namespaced because it reserves empty strings. */}
    <select aria-hidden="true" tabIndex={-1} className="sr-only" name={name} required={required} disabled={disabled} value={value} onChange={event => change(event.target.value)} onInvalid={event => { event.preventDefault(); setInvalid(true); trigger.current?.focus(); setOpen(true); }}>
      {children}
    </select>
    {invalid && <span id={errorId} role="alert" className="mt-1 block text-xs text-danger">Choose an option.</span>}
  </span>;
}
